// 引入 axios
import axios from "axios";
import store from "@/store";
import router from "@/router";

class Request {
  constructor(baseUrl = "") {
    this.baseUrl = baseUrl; // 基础 URL，用于拼接接口地址
    this.instance = axios.create({
      baseURL: this.baseUrl,
      timeout: 100000, // 请求超时时间
      headers: {
        "Content-Type": "application/json",
      },
    });

    // 添加请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        // 注入 Authorization header
        const token = store.state.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 添加响应拦截器
    this.instance.interceptors.response.use(
      (response) => {
        // 跳过 blob 类型的响应（文件下载）
        if (response.data instanceof Blob) {
          return Promise.resolve(response);
        }

        if (response.data.code !== 200) {
          return Promise.reject(new Error(response.data.message));
        }

        // 假设后端返回的都是 JSON 格式的字符串，这里统一处理
        if (typeof response.data.data === "string") {
          try {
            response.data.data = JSON.parse(response.data.data);
          } catch (error) {
            // 非 JSON 格式的字符串（如成功提示文字），保持原样
          }
        }
        return Promise.resolve(response);
      },
      (error) => {
        console.error("Response Error:", error);
        // 处理 401 未授权
        if (error.response && error.response.status === 401) {
          store.dispatch("clearAuth");
          if (router.currentRoute.path !== "/") {
            router.push("/");
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // 发起 GET 请求
  async get(endpoint, params = {}) {
    try {
      const response = await this.instance.get(endpoint, { params });
      return response.data; // 返回解析后的 JSON 数据
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 发起 PUT 请求
  async put(endpoint, data = {}) {
    try {
      const response = await this.instance.put(endpoint, data);
      return response.data; // 返回解析后的 JSON 数据
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 发起 POST 请求
  async post(endpoint, data = {}) {
    try {
      const response = await this.instance.post(endpoint, data);
      return response.data; // 返回解析后的 JSON 数据
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 发起 PATCH 请求
  async patch(endpoint, data = {}) {
    try {
      const response = await this.instance.patch(endpoint, data);
      return response.data;
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 发起 DELETE 请求
  async delete(endpoint) {
    try {
      const response = await this.instance.delete(endpoint);
      return response.data;
    } catch (error) {
      return this._handleError(error);
    }
  }

  // 处理错误
  _handleError(error) {
    if (error.response) {
      // 服务器返回错误状态码
      console.error(
        "Server Error:",
        error.response.status,
        error.response.data
      );
      return { error: error.response.data };
    } else if (error.request) {
      // 请求已发出，但未收到响应
      console.error("Request Error:", error.request);
      return { error: "No response received" };
    } else {
      // 其他错误
      console.error("Error:", error.message);
      return { error: error.message };
    }
  }
}

export default Request;

// // 使用示例
// const api = new Request("https://api.example.com");
//
// api
//   .get("/data", { id: 123 })
//   .then((data) => console.log("GET Response:", data))
//   .catch((error) => console.error("GET Error:", error));
//
// api
//   .post("/submit", { name: "John", age: 30 })
//   .then((data) => console.log("POST Response:", data))
//   .catch((error) => console.error("POST Error:", error));
